/**
 * SweepVoice — granular sweep player (public/worklets/sweep.js).
 *
 * Long, untouched real recordings (pull, overrun, pull-away, idle) with their rpm curves; the worklet
 * scrubs through them following the sim's rpm (ON and OFF streams crossfaded by load). See the worklet
 * header for the algorithm. Replaces RPMMixer when the car ships `sweeps.json`.
 */
export interface LoadedSweep {
  /** per-hop level curve (dB) of the take — the worklet corrects a loudness-ramp home toward the rpm's median level */
  lvl?: number[] | null;
  /** per-hop pitch-certification confidence (autocorr peak) — the worklet refuses to land where it is low */
  cconf?: number[] | null;
  id: string;
  mode: 'on' | 'off' | 'idle';
  hop: number;
  rpm: number[];
  /** recorded load estimate 0..1 (ON sweeps; −1 unknown) */
  thr?: number;
  /** only while the drivetrain reports lugging (rolling in gear under idle) */
  lug?: boolean;
  /** THE low bed: one continuous take that owns the whole low-speed range */
  low?: boolean;
  buffer: AudioBuffer;
}

export class SweepVoice {
  /** global cross-bed level reference (rpm buckets → dB) from sweeps.json */
  lowLvlRef: { start: number; step: number; db: number[] } | null = null;
  /** audio-thread starvation events reported by the worklet (chop that no DSP can fix) */
  glitches = 0;
  readonly output: GainNode;
  private node: AudioWorkletNode | null = null;
  private readonly ctx: BaseAudioContext;
  private readonly sweeps: LoadedSweep[];
  private static loaded = new WeakSet<BaseAudioContext>();

  static supported(): boolean {
    return typeof AudioWorkletNode !== 'undefined';
  }

  constructor(ctx: BaseAudioContext, sweeps: LoadedSweep[]) {
    this.ctx = ctx;
    this.sweeps = sweeps;
    this.output = ctx.createGain();
    this.output.gain.value = 1;
  }

  async start(): Promise<void> {
    const ctx = this.ctx;
    if (!SweepVoice.loaded.has(ctx)) {
      await ctx.audioWorklet.addModule('/worklets/sweep.js');
      SweepVoice.loaded.add(ctx);
    }
    const node = new AudioWorkletNode(ctx, 'sweep-player', { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [2] });
    for (const s of this.sweeps) {
      const data = new Float32Array(s.buffer.length);
      s.buffer.copyFromChannel(data, 0);
      const rpm = Float32Array.from(s.rpm);
      node.port.postMessage({ type: 'sweep', id: s.id, mode: s.mode, hop: s.hop, rpm, data, thr: s.thr ?? -1, lug: !!s.lug, low: !!s.low, lvl: s.lvl && s.lvl.length ? Float32Array.from(s.lvl) : null, cconf: s.cconf && s.cconf.length ? Float32Array.from(s.cconf) : null }, [rpm.buffer, data.buffer]);
      node.port.onmessage = (ev) => { if (ev.data?.type === 'glitches') this.glitches = ev.data.n; };
    if (this.lowLvlRef) node.port.postMessage({ type: 'lowLvlRef', ref: this.lowLvlRef });
    }
    node.connect(this.output);
    this.node = node;
  }

  /** rpm + load (0..1, ON↔OFF blend) + lug + clutch-slip flags, every frame */
  update(rpm: number, load: number, lug = false, slip = 0, jitter = false): void {
    if (!this.node) return;
    const now = this.ctx.currentTime;
    const p = this.node.parameters;
    p.get('rpm')?.setTargetAtTime(Math.max(50, rpm), now, 0.015);
    p.get('load')?.setTargetAtTime(Math.min(1, Math.max(0, load)), now, 0.04);
    p.get('lug')?.setValueAtTime(lug ? 1 : 0, now);
    p.get('slip')?.setValueAtTime(slip, now);
    p.get('jitter')?.setValueAtTime(jitter ? 1 : 0, now);
  }

  stop(): void {
    try { this.node?.port.postMessage({ type: 'clear' }); this.node?.disconnect(); } catch { /* ignore */ }
    this.node = null;
  }
}
